import { APIs } from '../../utilities/APIs';

/**
 * Management API utilities for users and groups
 * Provides centralized API calls with proper error handling
 * This is a function that takes the authenticated request function as a parameter
 */
export const createManagementAPI = (makeAuthenticatedRequest) => {

  // User Management APIs
  const userAPI = {
    // List all users with optional pagination
    listUsers: async (page = 1, limit = 20) => {
      try {
        const url = `${APIs.users.list}?page=${page}&limit=${limit}`;
        // Assuming makeAuthenticatedRequest handles response parsing and error throwing
        const responseData = await makeAuthenticatedRequest(url);
        
        return {
          success: true,
          data: responseData.users || [], // Correctly access the 'users' field
          message: responseData.message || 'Users retrieved successfully'
        };
      } catch (error) {
        // Assuming makeAuthenticatedRequest throws errors with a 'message' property
        return {
          success: false,
          error: error.message || 'Failed to fetch users',
          data: []
        };
      }
    },

    // Create a new user
    createUser: async (userData) => {
      try {
        // Assuming makeAuthenticatedRequest handles response parsing and error throwing
        const data = await makeAuthenticatedRequest(APIs.users.create, {
          method: 'POST',
          body: JSON.stringify(userData)
        });
        
        return {
          success: true,
          data: data.data || {},
          message: data.message || 'User created successfully'
        };
      } catch (error) {
        // Assuming makeAuthenticatedRequest throws errors with a 'message' property
        return {
          success: false,
          error: error.message || 'Failed to create user'
        };
      }
    },

    // Find user by email
    findUserByEmail: async (email) => {
      try {
        // This might need to call a specific backend endpoint for searching by email
        // For now, using listUsers and filtering locally as in the original code
        const users = await userAPI.listUsers(1, 1000); // Get all users for search
        if (users.success) {
          const user = users.data.find(u => 
            u.email?.toLowerCase() === email.toLowerCase() // Use user.email
          );
          return {
            success: true,
            data: user || null,
            message: user ? 'User found' : 'User not found'
          };
        }
        return users;
      } catch (error) {
        // Assuming makeAuthenticatedRequest throws errors with a 'message' property
        return {
          success: false,
          error: error.message || 'Error searching for user',
          data: null
        };
      }
    },

    // Find user by ID (UID)
    findUserById: async (uid) => {
      try {
        // This might need to call a specific backend endpoint for searching by UID
        // For now, using listUsers and filtering locally as in the original code
        const users = await userAPI.listUsers(1, 1000); // Get all users for search
        if (users.success) {
          const user = users.data.find(u => 
            u.uid === uid // Use user.uid
          );
          return {
            success: true,
            data: user || null,
            message: user ? 'User found' : 'User not found'
          };
        }
        return users;
      } catch (error) {
        // Assuming makeAuthenticatedRequest throws errors with a 'message' property
        return {
          success: false,
          error: error.message || 'Error searching for user',
          data: null
        };
      }
    },

    // Add user to group by email
    addUserToGroupByEmail: async (email, groupName) => {
      try {
        // Assuming makeAuthenticatedRequest handles response parsing and error throwing
        const data = await makeAuthenticatedRequest('/api/v1/users/addToGroup', {
          method: 'POST',
          body: JSON.stringify({ email, groupName })
        });
        
        return {
          success: true,
          message: data.message || 'User added to group successfully'
        };
      } catch (error) {
        // Assuming makeAuthenticatedRequest throws errors with a 'message' property
        return {
          success: false,
          error: error.message || 'Failed to add user to group'
        };
      }
    }
  };

  // Group Management APIs
  const groupAPI = {
    // List all groups
    listGroups: async () => {
      try {
        // Assuming makeAuthenticatedRequest handles response parsing and error throwing
        const data = await makeAuthenticatedRequest(APIs.groups.list);
        
        return {
          success: true,
          data: Array.isArray(data) ? data : [], // Assuming data is the array of groups
          message: 'Groups retrieved successfully' // Assuming no message in this response
        };
      } catch (error) {
        // Assuming makeAuthenticatedRequest throws errors with a 'message' property
        return {
          success: false,
          error: error.message || 'Failed to fetch groups',
          data: []
        };
      }
    },

    // Create a new group
    createGroup: async (groupName) => {
      try {
        // Assuming makeAuthenticatedRequest handles response parsing and error throwing
        const data = await makeAuthenticatedRequest(APIs.groups.create, {
          method: 'POST',
          body: JSON.stringify({ groupName })
        });
        
        return {
          success: true,
          data: data, // Assuming data is the created group object
          message: data.message || 'Group created successfully'
        };
      } catch (error) {
        // Assuming makeAuthenticatedRequest throws errors with a 'message' property
        return {
          success: false,
          error: error.message || 'Failed to create group'
        };
      }
    },

    // Delete a group
    deleteGroup: async (groupName) => {
      try {
        // Assuming makeAuthenticatedRequest handles response parsing and error throwing
        await makeAuthenticatedRequest(APIs.groups.delete(groupName), {
          method: 'DELETE'
        });
        
        return {
          success: true,
          message: 'Group deleted successfully' // Assuming no data returned on delete
        };
      } catch (error) {
        // Assuming makeAuthenticatedRequest throws errors with a 'message' property
        return {
          success: false,
          error: error.message || 'Failed to delete group'
        };
      }
    },

    // Get group details
    getGroupDetails: async (groupName) => {
      try {
        // Assuming makeAuthenticatedRequest handles response parsing and error throwing
        const data = await makeAuthenticatedRequest(APIs.groups.view(groupName));
        
        return {
          success: true,
          data: data, // Assuming data is the group details object
          message: 'Group details retrieved successfully' // Assuming no message in this response
        };
      } catch (error) {
        // Assuming makeAuthenticatedRequest throws errors with a 'message' property
        return {
          success: false,
          error: error.message || 'Failed to fetch group details'
        };
      }
    },

    // Find group by name
    findGroupByName: async (groupName) => {
      try {
        const groups = await groupAPI.listGroups();
        if (groups.success) {
          const group = groups.data.find(g => 
            g.name?.toLowerCase() === groupName.toLowerCase()
          );
          return {
            success: true,
            data: group || null,
            message: group ? 'Group found' : 'Group not found'
          };
        }
        return groups;
      } catch (error) {
        return {
          success: false,
          error: error.message || 'Error searching for group',
          data: null
        };
      }
    },

    // Add user to group
    addUserToGroup: async (groupName, userUID) => {
      try {
        // Assuming makeAuthenticatedRequest handles response parsing and error throwing
        const data = await makeAuthenticatedRequest(APIs.groups.addMember(groupName), {
          method: 'POST',
          body: JSON.stringify({ userUID })
        });
        
        return {
          success: true,
          message: data.message || 'User added to group successfully'
        };
      } catch (error) {
        // Assuming makeAuthenticatedRequest throws errors with a 'message' property
        return {
          success: false,
          error: error.message || 'Failed to add user to group'
        };
      }
    },

    // Remove user from group
    removeUserFromGroup: async (groupName, userUID) => {
      try {
        // Assuming makeAuthenticatedRequest handles response parsing and error throwing
        const data = await makeAuthenticatedRequest(APIs.groups.removeMember(groupName), {
          method: 'DELETE',
          body: JSON.stringify({ userUID })
        });
        
        return {
          success: true,
          message: data.message || 'User removed from group successfully'
        };
      } catch (error) {
        // Assuming makeAuthenticatedRequest throws errors with a 'message' property
        return {
          success: false,
          error: error.message || 'Failed to remove user from group'
        };
      }
    },

    // Add subgroup to group
    addSubgroupToGroup: async (parentGroupName, subGroupName) => {
      try {
        // Assuming makeAuthenticatedRequest handles response parsing and error throwing
        const data = await makeAuthenticatedRequest(APIs.groups.addSubgroup(parentGroupName), {
          method: 'POST',
          body: JSON.stringify({ subGroupName })
        });
        
        return {
          success: true,
          message: data.message || 'Subgroup added successfully'
        };
      } catch (error) {
        // Assuming makeAuthenticatedRequest throws errors with a 'message' property
        return {
          success: false,
          error: error.message || 'Failed to add subgroup'
        };
      }
    }
  };

  // Combined search functionality
  const searchAPI = {
    searchAll: async (query) => {
      try {
        const [usersResult, groupsResult] = await Promise.all([
          userAPI.listUsers(1, 1000),
          groupAPI.listGroups()
        ]);

        const users = usersResult.success ? usersResult.data.filter(user => {
          const email = user.email || ''; // Use user.email
          const uid = user.uid || ''; // Use user.uid
          const commonName = user.commonName || ''; // Use user.commonName
          return email.toLowerCase().includes(query.toLowerCase()) ||
                 uid.toLowerCase().includes(query.toLowerCase()) ||
                 commonName.toLowerCase().includes(query.toLowerCase());
        }) : [];

        const groups = groupsResult.success ? groupsResult.data.filter(group => 
          group.name?.toLowerCase().includes(query.toLowerCase()) ||
          group.description?.toLowerCase().includes(query.toLowerCase())
        ) : [];

        return {
          success: true,
          data: { users, groups },
          message: `Found ${users.length} users and ${groups.length} groups`
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || 'Error performing search',
          data: { users: [], groups: [] }
        };
      }
    }
  };

  return { userAPI, groupAPI, searchAPI };
};
